import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, STATUS_LABEL, hr, statusBadgeVariant } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { CalendarDays, Sun, FilePlus2, ArrowRight, HomeIcon, History, Clock, LogIn, LogOut, IdCard, BellRing, X } from "lucide-react";
import EmployeeHistorySheet from "../components/EmployeeHistorySheet";
import { DeanAuditWidget } from "../components/DeanAuditWidget";

function minutesToHm(m) {
  if (!m && m !== 0) return "—";
  const h = Math.floor(m / 60); const mm = m % 60;
  return `${h}h ${mm.toString().padStart(2, "0")}min`;
}

// Iter87: format seconds → "Xh YYmin ZZsek" — koristi se za live-ticking timer.
function secondsToHms(s) {
  if (s < 0) s = 0;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h}h ${m.toString().padStart(2, "0")}min ${sec.toString().padStart(2, "0")}sek`;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [mine, setMine] = useState([]);
  const [wfhUsage, setWfhUsage] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [att, setAtt] = useState(null);
  const [mySchedule, setMySchedule] = useState(null);
  const [notices, setNotices] = useState([]);
  // Iter72: attendance feature toggle — admin uvijek, ostali samo ako je uključeno
  const [attendanceVisible, setAttendanceVisible] = useState(true);

  useEffect(() => {
    api.get("/vacation/info").then(r => setInfo(r.data)).catch(() => {});
    api.get("/vacation/requests").then(r => setMine(r.data.filter(x => x.employee_email === user.email))).catch(() => {});
    api.get("/wfh/my-monthly-usage").then(r => setWfhUsage(r.data)).catch(() => {});
    api.get("/attendance/me/today").then(r => setAtt(r.data)).catch(() => {});
    api.get("/notifications/my-notices").then(r => setNotices(r.data || [])).catch(() => {});
    // Iter72 + Iter103: dohvat feature-flag postavke + per-user override
    if (user.role === "admin") { setAttendanceVisible(true); }
    else if (user.attendance_override_enabled) { setAttendanceVisible(true); }
    else {
      api.get("/settings").then(r => {
        setAttendanceVisible(r.data?.attendance_enabled_for_non_admin !== false);
      }).catch(() => setAttendanceVisible(true));
    }
  }, [user.email, user.role, user.attendance_override_enabled]);

  // Iter87: live-ticking timer za dashboard karticu "Prisutnost danas"
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (att?.current_state !== "in") return;
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [att?.current_state]);

  const liveSecondsToday = (() => {
    const baseSec = (att?.minutes_worked_today || 0) * 60;
    if (att?.current_state !== "in" || !att?.last_checkin_ts) return baseSec;
    try {
      const inMs = new Date(att.last_checkin_ts).getTime();
      const delta = Math.max(0, Math.floor((nowMs - inMs) / 1000));
      return baseSec + delta;
    } catch { return baseSec; }
  })();

  const dismissNotice = (id) => {
    setNotices(prev => prev.filter(n => n.id !== id));
    api.post(`/notifications/my-notices/${id}/dismiss`).catch(() => {});
  };

  useEffect(() => {
    // Load assigned schedule once (separate fetch — keeps lint happy)
    if (!user.work_schedule_id) return;
    api.get("/work-schedules")
      .then(r => setMySchedule((r.data || []).find(s => s.id === user.work_schedule_id) || null))
      .catch(() => {});
  }, [user.work_schedule_id]);

  return (
    <div className="space-y-8" data-testid="dashboard">
      <section>
        <div className="flex items-end justify-between flex-wrap gap-4 mb-2">
          <div>
            <p className="text-sm text-muted-foreground uppercase tracking-wider">Dobrodošli</p>
            <h1 className="serif text-4xl sm:text-5xl font-semibold">{user.full_name}</h1>
            <p className="text-muted-foreground mt-1">{user.katedra}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="lg" variant="ghost" onClick={() => setShowHistory(true)} data-testid="dashboard-my-history">
              <History className="w-4 h-4 mr-2" /> Moja povijest
            </Button>
            <Link to="/wfh/novi">
              <Button size="lg" variant="outline" data-testid="dashboard-new-wfh">
                <HomeIcon className="w-4 h-4 mr-2" /> Zahtjev za rad na daljinu
              </Button>
            </Link>
            <Link to="/zahtjev">
              <Button size="lg" data-testid="dashboard-new-request">
                <FilePlus2 className="w-4 h-4 mr-2" /> Predaj zahtjev za godišnji odmor
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Iter65: osobne obavijesti (npr. izdano kolektivno rješenje) */}
      {notices.length > 0 && (
        <section className="space-y-3" data-testid="user-notices">
          {notices.map(n => (
            <div key={n.id} data-testid="user-notice-banner"
                 className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4">
              <BellRing className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-amber-900">Novo rješenje o godišnjem odmoru</div>
                <p className="text-sm text-amber-800 mt-0.5" data-testid="user-notice-message">{n.message}</p>
                <p className="text-[11px] text-amber-700/70 mt-1">
                  {n.created_at ? new Date(n.created_at).toLocaleDateString("hr-HR") : ""}
                </p>
              </div>
              <button onClick={() => dismissNotice(n.id)} data-testid="user-notice-dismiss"
                      className="p-1 rounded hover:bg-amber-100 text-amber-700 transition-colors"
                      aria-label="Zatvori obavijest">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </section>
      )}

      {/* Dean / admin / secretary — operational pulse widget (Iter20) */}
      {(user.role === "dean" || user.role === "admin" || user.role === "secretary") && (
        <DeanAuditWidget />
      )}

      {/* My attendance widget (Iter29) — only shows if user has the feature enabled (work_schedule_id or rfid_card_uid set) OR has any scan today */}
      {attendanceVisible && (user.work_schedule_id || att?.events_today?.length > 0) && (
        <Card data-testid="dash-attendance-widget" className="border-l-4 border-l-accent">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary"/>Moj raspored danas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-center">
              {/* Big state badge */}
              <div className="flex items-center gap-3">
                {att?.current_state === "in" ? (
                  <>
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                      <LogIn className="w-6 h-6 text-green-700"/>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-muted-foreground">Status</div>
                      <div className="text-lg font-semibold text-green-700" data-testid="dash-att-state">Prijavljeni</div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
                      <LogOut className="w-6 h-6 text-muted-foreground"/>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-muted-foreground">Status</div>
                      <div className="text-lg font-semibold text-muted-foreground" data-testid="dash-att-state">Odjavljeni</div>
                    </div>
                  </>
                )}
              </div>
              {/* Worked today */}
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Odrađeno danas</div>
                <div className="text-2xl font-semibold tabular-nums" data-testid="dash-att-worked">
                  {att?.current_state === "in"
                    ? secondsToHms(liveSecondsToday)
                    : minutesToHm(att?.minutes_worked_today || 0)}
                </div>
                {att?.overtime_minutes_today > 0 && (
                  <div className="text-[11px] mt-1 text-amber-700 font-medium" data-testid="dash-att-overtime">
                    Prekovremeno: {minutesToHm(att.overtime_minutes_today)}
                  </div>
                )}
                {att?.events_today?.length > 0 && (
                  <div className="text-[11px] text-muted-foreground mt-1">
                    Današnji: {att.events_today.length} {att.events_today.length === 1 ? "događaj" : "događaja"}
                  </div>
                )}
              </div>
              {/* Schedule + scan link */}
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Vaš raspored</div>
                <div className="text-sm font-medium" data-testid="dash-att-schedule">
                  {mySchedule
                    ? (mySchedule.flexible
                        ? "Fleksibilan"
                        : `${mySchedule.start_time}–${mySchedule.end_time}`)
                    : "—"}
                </div>
                {mySchedule && !mySchedule.flexible && (
                  <div className="text-[11px] text-muted-foreground">
                    Tolerancija kašnjenja: {mySchedule.max_late_min ?? 15} min
                  </div>
                )}
                <Link to="/prisutnost" className="inline-block mt-2">
                  <Button size="sm" variant="outline" data-testid="dash-att-link">
                    <IdCard className="w-3.5 h-3.5 mr-1"/>Skeniraj karticom / Povijest
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {info?.collective_enabled !== false && (
      <Card className="bg-primary text-primary-foreground border-primary/40 overflow-hidden relative" data-testid="dashboard-collective-card">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage:"radial-gradient(circle at 90% 10%, hsl(var(--accent)) 0, transparent 30%)"}}/>
        <CardHeader className="relative">
          <div className="flex items-center gap-2 text-accent text-xs uppercase tracking-widest mb-2">
            <Sun className="w-4 h-4" /> Obavijest
          </div>
          <CardTitle className="serif text-3xl">Kolektivni godišnji odmor 2026.</CardTitle>
        </CardHeader>
        <CardContent className="relative">
          <p className="text-lg leading-relaxed opacity-90 mb-6 max-w-3xl whitespace-pre-wrap">
            {info?.message || "Učitavanje..."}
          </p>
          <div className="flex flex-wrap gap-6">
            <div>
              <div className="text-xs uppercase tracking-wider opacity-60">Početak</div>
              <div className="serif text-2xl font-semibold mt-1">{info ? hr(info.collective_start) : "—"}</div>
            </div>
            <div className="w-px bg-primary-foreground/20" />
            <div>
              <div className="text-xs uppercase tracking-wider opacity-60">Kraj</div>
              <div className="serif text-2xl font-semibold mt-1">{info ? hr(info.collective_end) : "—"}</div>
            </div>
            <div className="w-px bg-primary-foreground/20" />
            <div>
              <div className="text-xs uppercase tracking-wider opacity-60">Radnih dana</div>
              <div className="serif text-2xl font-semibold mt-1">{info?.collective_workdays ?? "—"}</div>
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      <section className="grid md:grid-cols-3 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-base">Vaš godišnji odmor — preostalo</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <span className="serif text-5xl font-semibold text-accent">
                {Math.max(0, (user.ukupni_fond_dana ?? 0) - (user.iskoristeno_dana_effective ?? user.iskoristeno_dana ?? 0))}
              </span>
              <span className="text-muted-foreground text-sm">radnih dana</span>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Iskorišteno: <b>{user.iskoristeno_dana_effective ?? user.iskoristeno_dana ?? 0}</b> dana
              {" "}
              {user.iskoristeno_dana_manual_override
                ? <span className="text-amber-700">(početno stanje + zahtjevi)</span>
                : <span className="text-emerald-700">(auto)</span>}
            </p>
            <p className="text-[11px] text-muted-foreground mt-1">
              Ukupni fond za 2026.: <b>{user.ukupni_fond_dana}</b> radnih dana
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><HomeIcon className="w-4 h-4 text-accent"/>Rad od kuće — ovaj mjesec</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <span className="serif text-5xl font-semibold">{wfhUsage?.current_month?.used ?? 0}</span>
              <span className="text-muted-foreground text-sm">/ {wfhUsage?.monthly_cap ?? 2} dana</span>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Preostalo: <b>{wfhUsage?.current_month?.remaining ?? 2}</b> radnih dana · {wfhUsage?.current_month?.month || "—"}
            </p>
            <p className="text-[11px] text-muted-foreground mt-1">
              Ukupno odobreno {wfhUsage?.year ?? new Date().getFullYear()}: <b>{wfhUsage?.year_total ?? 0}</b> dana
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">Vaši zahtjevi GO</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <span className="serif text-5xl font-semibold">{mine.length}</span>
              <span className="text-muted-foreground text-sm">ukupno predano</span>
            </div>
            <Link to="/zahtjevi" className="text-xs text-primary inline-flex items-center mt-2">Pogledaj <ArrowRight className="w-3 h-3 ml-1" /></Link>
          </CardContent>
        </Card>
      </section>

      {mine.length > 0 && (
        <section>
          <h2 className="serif text-2xl font-semibold mb-3">Vaši nedavni zahtjevi</h2>
          <div className="space-y-3">
            {mine.slice(0,5).map(r => (
              <Link key={r.id} to={`/zahtjev/${r.id}`} className="block">
                <Card className="hover:border-accent transition-colors">
                  <CardContent className="p-4 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-4 min-w-0">
                      <CalendarDays className="w-5 h-5 text-primary shrink-0" />
                      <div className="min-w-0">
                        <div className="font-medium truncate">{hr(r.collective_start)} – {hr(r.collective_end)} + {r.additional_periods?.length || 0} dodatnih</div>
                        <div className="text-xs text-muted-foreground">Ukupno {r.total_days} radnih dana · predano {hr(r.created_at)}</div>
                      </div>
                    </div>
                    <Badge variant={statusBadgeVariant(r.status)} data-testid={`status-${r.id}`}>{STATUS_LABEL[r.status]}</Badge>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </section>
      )}

      <EmployeeHistorySheet
        employee={showHistory ? {
          email: user.email,
          full_name: user.full_name,
          academic_title: user.academic_title || user.title,
          katedra: user.katedra,
          radno_mjesto: user.radno_mjesto,
        } : null}
        onClose={() => setShowHistory(false)}
      />
    </div>
  );
}
